import { Permission } from '@customer/permissions';
import { customerApi, QueryTag } from '../api';
import { TagDescription } from '@reduxjs/toolkit/query';

export enum MapProviderType {
  OpenStreetMap = 'OpenStreetMap',
  RasterOnly = 'RasterOnly',
  RasterWithOpenStreetMap = 'RasterWithOpenStreetMap',
  ArcGIS = 'ArcGIS',
  GeoServer = 'GeoServer',
}

export interface MapProviderModel {
  providerType: MapProviderType;
  uri?: string;
  layerName?: string;
  maxZoom?: number;
  maxNativeZoom?: number;
}

export interface SiteContextModel {
  id: string; // Site ID
  name: string;
}

export interface SiteModel extends SiteContextModel {
  timezone: string;
  map: MapProviderModel;
}

export enum MeasurementUnit {
  Metric = 'Metric',
  USCustomary = 'USCustomary',
}

export interface MeModel {
  displayName: string;
  firstName: string;
  lastName: string;
  initials: string;
  permissions: Permission[];
  siteContexts: SiteContextModel[];
  dateTimeNumberFormat: string;
  units: MeasurementUnit;
  currentSite?: SiteModel;
  appVersion: string;
  hideWelcomeDialog: boolean;
}

const authTags: TagDescription<QueryTag.UserAuth>[] = [{ type: QueryTag.UserAuth, id: 'LIST' }];

export const authApi = customerApi.injectEndpoints({
  endpoints: build => ({
    me: build.query<MeModel, void>({
      query: () => 'api/me',
      // The auth state must be invalidated when the site changes.
      providesTags: [QueryTag.SiteCache, ...authTags],
    }),
    changeSite: build.mutation<unknown, string>({
      query: request => ({
        url: 'api/me/site',
        method: 'POST',
        body: {
          siteId: request,
        },
      }),
      // All endpoint responses tagged with SiteCache must be invalidated when the site switches.
      invalidatesTags: [QueryTag.SiteCache],
    }),
    hideWelcome: build.mutation<unknown, void>({
      query: () => ({
        url: 'api/me/hidewelcome',
        method: 'POST',
      }),
      // Note: Do not invalidate auth tags or this might trigger an unecessary refresh.
      // The correct hideWelcomeDialog value will be available in the next session, which will prevent the dialog being displayed.
    }),
  }),
  overrideExisting: false,
});

export const { useMeQuery, useChangeSiteMutation, useHideWelcomeMutation } = authApi;
